import { DateTime } from 'luxon'

/**
 * Gets the display date/time for a planned call based on the showScheduledCallsDueDate FF.
 *
 * When FF ON: Uses dueDate as the intended day, and if dueDatetime is present,
 *             extracts the local time from dueDatetime and combines it with dueDate.
 *             This ensures the intended calendar day is preserved regardless of timezone.
 *
 * When FF OFF: Uses dueDatetime only (original behavior for backwards compatibility).
 * @param plannedCall - The planned call object with dueDate and dueDatetime
 * @param plannedCall.dueDate - The intended calendar date (YYYY-MM-DD)
 * @param plannedCall.dueDatetime - The scheduled datetime in UTC (ISO string)
 * @param showScheduledCallsDueDate - Feature flag value
 * @returns ISO string in UTC for display, or empty string if no date available
 * @example
 * // FF ON with time set (Eastern timezone example)
 * // If user scheduled 2:45 PM on 2026-09-18 local time:
 * getPlannedCallDisplayDateTime(
 *   { dueDate: '2026-09-18', dueDatetime: '2026-09-18T18:45:00.000Z' },
 *   true
 * )
 * // Returns: '2026-09-18T18:45:00.000Z' (date from dueDate, time from dueDatetime converted through local)
 * @example
 * // FF ON with date only (no time)
 * getPlannedCallDisplayDateTime(
 *   { dueDate: '2026-09-18', dueDatetime: undefined },
 *   true
 * )
 * // Returns: '2026-09-18'
 * @example
 * // FF OFF (legacy behavior - returns dueDatetime as-is)
 * getPlannedCallDisplayDateTime(
 *   { dueDate: '2026-09-18', dueDatetime: '2026-09-18T18:45:00.000Z' },
 *   false
 * )
 * // Returns: '2026-09-18T18:45:00.000Z'
 */
export function getPlannedCallDisplayDateTime(
  plannedCall: { dueDate: string; dueDatetime?: string | null | undefined },
  showScheduledCallsDueDate: boolean
): string {
  // CLEANUP (MET-2966): Remove FF check and always use dueDate + dueDatetime pattern
  if (showScheduledCallsDueDate) {
    const { dueDate, dueDatetime } = plannedCall

    if (dueDatetime) {
      // Combine date from dueDate with time from dueDatetime
      // Convert dueDatetime to local timezone first to extract the local time
      const dateOnly = DateTime.fromISO(dueDate, { zone: 'local' })
      const timeFromDatetime = DateTime.fromISO(dueDatetime, {
        zone: 'utc',
      }).toLocal()

      const combined = dateOnly.set({
        hour: timeFromDatetime.hour,
        minute: timeFromDatetime.minute,
        second: timeFromDatetime.second,
      })

      // toISO() returns string | null, but only returns null for invalid DateTime objects.
      // Since we're parsing validated backend ISO strings, the DateTime is guaranteed valid.
      return combined.toUTC().toISO() as string
    }

    // Date-only (no time set)
    return dueDate
  }

  // FF OFF: Use dueDatetime (original behavior)
  return plannedCall.dueDatetime || ''
}
